import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  BaseRouteReuseStrategy,
  Router,
  RunGuardsAndResolvers,
} from '@angular/router';
import { FOCUS_CONTENT_NAVIGATION } from './focus-content';

/** Route `data` keys for the org-switch page reload. */
export const ORG_SWITCH_DATA = {
  /** On the authenticated shell route: it survives the reload; every page below it is
   *  re-created. */
  reloadRoot: 'orgSwitchReloadRoot',
  /** On a page tied to one org's record (a user, a location): where to go instead of
   *  reloading it, since the record doesn't exist in the new org. */
  redirect: 'orgSwitchRedirect',
} as const;

/**
 * Whether an org-switch reload navigation is running. Module-level (behind
 * {@link OrgSwitchReloadState}) because {@link RERUN_GUARDS_ON_ORG_SWITCH} is called by
 * the Router outside an injection context.
 */
let orgSwitchReloading = false;

/**
 * Set on every guarded route below the shell so its guards (role, data type, Location
 * Management) re-check the new org on the reload. Angular re-runs a guard only when
 * its route config changes or this says so — the reuse strategy alone doesn't.
 * Otherwise it keeps Angular's default (`paramsChange`): a `?tab=` or other query
 * change doesn't re-run the guards.
 */
export const RERUN_GUARDS_ON_ORG_SWITCH: RunGuardsAndResolvers = (curr, future) =>
  orgSwitchReloading || !sameParamsAndPath(curr, future);

function sameParamsAndPath(a: ActivatedRouteSnapshot, b: ActivatedRouteSnapshot): boolean {
  const aKeys = Object.keys(a.params);
  return (
    aKeys.length === Object.keys(b.params).length &&
    aKeys.every((key) => a.params[key] === b.params[key]) &&
    a.url.map((s) => s.path).join('/') === b.url.map((s) => s.path).join('/')
  );
}

/**
 * Where to send the user after an org switch instead of reloading the page: the
 * `orgSwitchRedirect` of the deepest active route that has one, else `null`.
 */
export function orgSwitchRedirect(root: ActivatedRouteSnapshot): string | null {
  let redirect: string | null = null;
  for (let route: ActivatedRouteSnapshot | null = root; route; route = route.firstChild) {
    const target = route.routeConfig?.data?.[ORG_SWITCH_DATA.redirect] as string | undefined;
    if (target) {
      redirect = target;
    }
  }
  return redirect;
}

/**
 * Whether an org-switch reload navigation is running. Kept apart from
 * {@link OrgSwitchReloadService} (which needs the Router) because the reuse strategy
 * is built by the Router itself — injecting the service there would be a cycle.
 */
@Injectable({ providedIn: 'root' })
export class OrgSwitchReloadState {
  get reloading(): boolean {
    return orgSwitchReloading;
  }

  set reloading(value: boolean) {
    orgSwitchReloading = value;
  }
}

/**
 * Reloads the open page after an org switch: navigates to it again (or to its
 * `orgSwitchRedirect`) while {@link OrgSwitchReuseStrategy} re-creates every page below
 * the shell, so each one re-runs its own initial loads — and its guards — for the new
 * org. Pages need no org-change handling of their own. The shell (top bar, side-nav)
 * stays; focus moves to the page content, as after a side-nav selection.
 */
@Injectable({ providedIn: 'root' })
export class OrgSwitchReloadService {
  private readonly router = inject(Router);
  private readonly state = inject(OrgSwitchReloadState);

  async reloadActivePage(): Promise<void> {
    const url = orgSwitchRedirect(this.router.routerState.snapshot.root) ?? this.router.url;
    this.state.reloading = true;
    try {
      await this.router.navigateByUrl(url, {
        ...FOCUS_CONTENT_NAVIGATION,
        onSameUrlNavigation: 'reload',
        // The previous org's page isn't somewhere Back should return to.
        replaceUrl: true,
      });
    } finally {
      this.state.reloading = false;
    }
  }
}

/**
 * Angular's default reuse, except during an org-switch reload: then every route below
 * the shell (the route marked `orgSwitchReloadRoot`) is re-created instead of reused.
 * The flag is read from the route config, not `data` — child routes inherit an
 * empty-path parent's `data`.
 */
@Injectable({ providedIn: 'root' })
export class OrgSwitchReuseStrategy extends BaseRouteReuseStrategy {
  private readonly state = inject(OrgSwitchReloadState);

  override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (this.state.reloading && isBelowReloadRoot(future)) {
      return false;
    }
    return super.shouldReuseRoute(future, curr);
  }
}

function isBelowReloadRoot(route: ActivatedRouteSnapshot): boolean {
  for (let parent = route.parent; parent; parent = parent.parent) {
    if (parent.routeConfig?.data?.[ORG_SWITCH_DATA.reloadRoot] === true) {
      return true;
    }
  }
  return false;
}
